import React, { useState, useCallback, useEffect } from "react";
import {
  Grid,
  TextField,
  Alert,
  Typography,
  Box,
  MenuItem,
  Button,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import debounce from "lodash/debounce";
import { AllocationCategory } from "../types";
import { commonTextFieldProps, smallButtonProp } from "../../../utils/styles";
import { useSearchQuery } from "../../../hooks/useSearchQuery";
import { SearchableEntity } from "../../../services/searchService";
import { formatText, formatVehicleNumber } from "../../../utils/utils";
import SearchableAutocomplete from "../../../components/SearchableAutocomplete";

interface ReferenceOption {
  id: string;
  name: string;
  [key: string]: any;
}

interface ReferenceSelectionFieldsProps {
  category: AllocationCategory;
  subCategory: string;
  onReferenceChange: (
    reference: ReferenceOption | null,
    customData?: any
  ) => void;
  selectedReference: ReferenceOption | null;
  disabled?: boolean;
}

// Map category and subcategory to SearchableEntity
const getSearchableEntity = (
  category: AllocationCategory,
  subCategory: string
): SearchableEntity | null => {
  switch (category) {
    case AllocationCategory.VEHICLE:
      return "VEHICLE"; // Same for BUS and TRUCK

    case AllocationCategory.RESOURCE:
      switch (subCategory) {
        case "DRIVER":
          return "DRIVER";
        case "CLEANER":
          return "CLEANER";
        case "LADY":
          return "LADY";
        case "MANAGER":
          return "MANAGER";
        case "AREA_MANAGER":
          return "AREA_MANAGER";
        case "CLUSTER_LEADER":
          return "CLUSTER_LEADER";
        default:
          return null;
      }

    default:
      return null;
  }
};

const getRequiredFields = (
  category: AllocationCategory,
  subCategory: string,
  acquisitionType?: string
) => {
  switch (category) {
    case AllocationCategory.VEHICLE:
      // Base required fields for all acquisition types
      const baseFields = [
        {
          name: "vehicleType",
          label: "Vehicle Type",
          type: "select",
          required: true,
          defaultValue: subCategory === "BUS" ? "Bus" : "Truck",
          options: ["Bus", "Truck"],
          disabled: true, // Always disabled, auto-populated from subcategory
        },
        {
          name: "acquisitionType",
          label: "Acquisition Type",
          type: "select",
          required: true,
          options: ["New Purchase", "Used Purchase", "Leased", "Outsourced"],
        },
        {
          name: "vehicleNumber",
          label: "Vehicle Number (RC)",
          type: "text",
          required: true,
          placeholder: "e.g., MH01AA1111",
          maxLength: 10,
        },
        {
          name: "fuelType",
          label: "Fuel Type",
          type: "select",
          required: true,
          options: ["Diesel", "CNG", "Electric", "Petrol"],
        },
      ];

      // For other acquisition types, include all fields
      return baseFields;

    case AllocationCategory.RESOURCE:
      return [
        {
          name: "type",
          label: subCategory,
          defaultValue: subCategory,
          type: "select",
          required: true,
          options: ["DRIVER", "CLEANER", "LADY", "MANAGER", "AREA_MANAGER", "CLUSTER_LEADER"],
          disabled: true,
        },
        { name: "name", label: "Full Name", type: "text", required: true },
        {
          name: "contactNumber",
          label: "Phone Number",
          type: "tel",
          required: true,
        },
        {
          name: "employmentType",
          label: "Employment Type",
          type: "select",
          required: true,
          options: ["Permanent", "Temporary"],
        },
        {
          name: "aadharNumber",
          label: "Aadhar Number",
          type: "text",
          required: true,
        },
        {
          name: "drivingLicense",
          label: "Driving License Number",
          type: "text",
          required: subCategory === "DRIVER" ? true : false,
        },
        { name: "address", label: "Address", type: "text", required: false },
      ];
      return [];

    default:
      return [];
  }
};

// Get additional fields for vehicles - optional for Outsourced, required for others
const getAdditionalFields = (
  category: AllocationCategory,
  subCategory: string,
  acquisitionType?: string
) => {
  if (category === AllocationCategory.VEHICLE) {
    // For Outsourced, these fields are optional; for others, they are required
    const isMarketLoad = acquisitionType === "Outsourced";

    return [
      {
        name: "makeYear",
        label: "Make Year",
        type: "select",
        required: !isMarketLoad,
        options: Array.from(
          { length: new Date().getFullYear() - 1989 },
          (_, i) => String(1990 + i)
        ).reverse(),
      },
      {
        name: "model",
        label: "Model Name",
        type: "text",
        required: !isMarketLoad,
      },
      {
        name: "color",
        label: "Color",
        type: "text",
        required: !isMarketLoad,
      },
      {
        name: "chassisNumber",
        label: "Chassis Number",
        type: "text",
        required: !isMarketLoad,
        maxLength: 20,
      },
      {
        name: "acType",
        label: "AC / Non AC",
        type: "select",
        required: !isMarketLoad,
        options: ["AC", "Non-AC"],
      },
      {
        name: "bodyType",
        label: "Sleeper / Seater",
        type: "select",
        required: !isMarketLoad,
        options: ["Sleeper", "Seater", "Semi Sleeper"],
      },
      {
        name: "configuration",
        label: "Configuration",
        type: "select",
        required: !isMarketLoad,
        dependsOn: "bodyType",
        getOptions: (formData: any) => {
          const bodyType = formData?.bodyType;
          if (bodyType === "Sleeper") return ["1*1", "2*1", "2*2", "Other"];
          if (bodyType === "Semi Sleeper") return ["2*1", "2*2", "Other"];
          if (bodyType === "Seater") return ["2*1", "2*2", "2*3", "Other"];
          return [];
        },
      },
      {
        name: "seatingCapacity",
        label: "Seating Capacity",
        type: "number",
        required: !isMarketLoad,
        min: 1,
        max: 999,
      },
    ];
  }
  return [];
};

const ReferenceSelectionFields: React.FC<ReferenceSelectionFieldsProps> = ({
  category,
  subCategory,
  onReferenceChange,
  selectedReference,
  disabled = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [customData, setCustomData] = useState<any>({});
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  // Search states for owner fields
  const [ownerSearchTerm, setOwnerSearchTerm] = useState("");
  const { data: ownerOptions = [], isLoading: ownerLoading } = useSearchQuery(
    "OUTSOURCED_OWNER" as SearchableEntity,
    ownerSearchTerm
  );

  const debouncedOwnerSearch = useCallback(
    debounce((term: string) => setOwnerSearchTerm(term), 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedOwnerSearch.cancel();
    };
  }, [debouncedOwnerSearch]);

  const acquisitionType = customData?.acquisitionType || "";
  const requiredFields = getRequiredFields(
    category,
    subCategory,
    acquisitionType
  );
  const additionalFields = getAdditionalFields(
    category,
    subCategory,
    acquisitionType
  );
  const searchableEntity = getSearchableEntity(category, subCategory);

  const isMarketLoad = acquisitionType === "Outsourced";
  const showAdditionalFieldsDirectly =
    !isMarketLoad && additionalFields.length > 0;

  const getOptionLabel = (option: any) => {
    if (typeof option === "string") return option;
    return (
      option?.name || option?.vehicleNumber || option?.petrolPumpName || ""
    );
  };

  const handleSelect = (option: any) => {
    if (option) {
      setIsManualEntry(false);
      setCustomData({});
      setSearchValue(getOptionLabel(option));
      onReferenceChange(option);
    }
  };

  const handleInputChange = (value: string) => {
    onReferenceChange(null);
    setSearchValue(value);
    if (!value) {
      setIsManualEntry(false);
      setCustomData({});
      onReferenceChange(null);
    }
  };

  const handleManualEntry = () => {
    if (!selectedReference) {
      setIsManualEntry(true);
      const initialCustomData: any = {};
      requiredFields.forEach((field: any) => {
        if (field.name === "name" || field.name === "vehicleNumber") {
          initialCustomData[field.name] = searchValue;
        } else if (field.defaultValue) {
          initialCustomData[field.name] = field.defaultValue;
        } else {
          initialCustomData[field.name] = "";
        }
      });
      setCustomData(initialCustomData);
      onReferenceChange(null, initialCustomData);
    }
  };

  const renderOptionContent = (option: any) => {
    switch (searchableEntity) {
      case "VEHICLE":
        return (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              {formatVehicleNumber(option?.vehicleNumber)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.55rem",
                color: "text.secondary",
                backgroundColor: "grey.100",
                px: 1,
                py: 0.25,
                borderRadius: 0.5,
              }}
            >
              {option?.acquisitionType}
            </Typography>
          </Box>
        );
      case "DRIVER":
      case "CLEANER":
      case "LADY":
      case "MANAGER":
      case "AREA_MANAGER":
      case "CLUSTER_LEADER":
        return (
          <>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.75rem", fontWeight: 500 }}
            >
              {option?.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.65rem" }}
            >
              {option?.contactNumber}
            </Typography>
          </>
        );
      default:
        return (
          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
            {getOptionLabel(option)}
          </Typography>
        );
    }
  };

  const handleCustomFieldChange = (fieldName: string, value: string) => {
    const updatedCustomData = { ...customData, [fieldName]: value };

    // Handle dependent field resets
    if (fieldName === "bodyType") {
      updatedCustomData.configuration = ""; // Reset configuration when bodyType changes
    }

    // Reset optional fields visibility when acquisition type changes
    if (fieldName === "acquisitionType") {
      setShowOptionalFields(false);
    }

    setCustomData(updatedCustomData);
    onReferenceChange(null, updatedCustomData);
  };

  if (!category || !subCategory) {
    return (
      <Grid item xs={12}>
        <Alert severity="info" sx={{ fontSize: "0.75rem" }}>
          Please select category and type first to choose reference.
        </Alert>
      </Grid>
    );
  }

  if (!searchableEntity) {
    return (
      <Grid item xs={12}>
        <Alert severity="warning" sx={{ fontSize: "0.75rem" }}>
          This combination is not supported yet.
        </Alert>
      </Grid>
    );
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={3}>
        <SearchableAutocomplete
          entityType={searchableEntity}
          value={searchValue || selectedReference || null}
          onSelect={handleSelect}
          onChange={handleInputChange}
          label={`Select ${subCategory.toLowerCase()}`}
          placeholder="Search or enter manually..."
          getOptionLabel={getOptionLabel}
          renderOptionContent={renderOptionContent}
          noOptionsText={
            searchValue
              ? `No ${subCategory.toLowerCase()} found. Click on the text to add manually.`
              : "Start typing to search..."
          }
          disabled={disabled}
          size="small"
        />
      </Grid>
      <Grid item xs={12} md={9}>
        {!selectedReference && !isManualEntry && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              color="primary"
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                fontSize: "0.85rem",
                fontWeight: 700,
              }}
              onClick={handleManualEntry}
            >
              Add new {formatText(subCategory)}
            </Typography>
          </Box>
        )}
      </Grid>
      {isManualEntry && (
        <Grid item xs={12}>
          <Alert
            severity="info"
            sx={{ mb: 1.7, fontSize: "0.75rem", padding: "0.5rem" }}
          >
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              <strong>Manual Entry Mode:</strong> Adding new{" "}
              {formatText(subCategory)}. Fill in the required details below.
            </Typography>
          </Alert>
          <Grid container spacing={1.5}>
            {requiredFields.map((field: any) => {
              // Handle dependent fields
              if (field.dependsOn) {
                const dependentValue = customData[field.dependsOn];
                if (!dependentValue || dependentValue === "Select") {
                  return null; // Don't render if dependency is not met
                }
              }

              if (field.name === "address") {
                return (
                  <Grid item xs={12} sm={6} md={12} key={field.name}>
                    <TextField
                      label={field.label}
                      value={customData[field.name] || ""}
                      onChange={(e) =>
                        handleCustomFieldChange(field.name, e.target.value)
                      }
                      fullWidth
                      required={field.required}
                      disabled={disabled || field.disabled}
                      {...commonTextFieldProps}
                      multiline
                    />
                  </Grid>
                );
              }

              return (
                <Grid item xs={12} sm={6} md={2} key={field.name}>
                  {field.type === "select" ? (
                    <TextField
                      select
                      label={field.label}
                      value={customData[field.name] || ""}
                      onChange={(e) =>
                        handleCustomFieldChange(field.name, e.target.value)
                      }
                      fullWidth
                      required={field.required}
                      disabled={
                        disabled ||
                        field.disabled ||
                        (field.dependsOn && !customData[field.dependsOn])
                      }
                      {...commonTextFieldProps}
                    >
                      <MenuItem value="" disabled>
                        Select {field.label}
                      </MenuItem>
                      {(field.getOptions
                        ? field.getOptions(customData)
                        : field.options || []
                      ).map((option: string) => (
                        <MenuItem
                          key={option}
                          value={option}
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : field.type === "number" ? (
                    <TextField
                      label={field.label}
                      type="number"
                      value={customData[field.name] || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (
                          field.max &&
                          value.length > String(field.max).length
                        )
                          return;
                        handleCustomFieldChange(field.name, value);
                      }}
                      fullWidth
                      required={field.required}
                      disabled={disabled || field.disabled}
                      inputProps={{
                        min: field.min || undefined,
                        max: field.max || undefined,
                      }}
                      {...commonTextFieldProps}
                    />
                  ) : (
                    <TextField
                      label={field.label}
                      type={field.type || "text"}
                      value={customData[field.name] || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (field.maxLength && value.length > field.maxLength)
                          return;
                        handleCustomFieldChange(field.name, value);
                      }}
                      fullWidth
                      required={field.required}
                      disabled={disabled || field.disabled}
                      placeholder={field.placeholder}
                      inputProps={{
                        maxLength: field.maxLength || undefined,
                      }}
                      {...commonTextFieldProps}
                    />
                  )}
                </Grid>
              );
            })}
          </Grid>

          {/* Market Load specific SearchableAutocomplete fields for Outsourced type */}
          {isMarketLoad && (
            <Grid container spacing={1.5} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={ownerOptions}
                  loading={ownerLoading}
                  value={customData.ownerCompanyName || null}
                  freeSolo
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return option?.companyName || option?.name || "";
                  }}
                  onChange={(_, newValue) => {
                    if (typeof newValue === "string") {
                      handleCustomFieldChange("ownerCompanyName", newValue);
                      debouncedOwnerSearch(newValue);
                    } else if (newValue) {
                      // Update all fields from selected option
                      handleCustomFieldChange("ownerId", newValue.id || "");
                      handleCustomFieldChange(
                        "ownerCompanyName",
                        newValue.companyName || ""
                      );
                      handleCustomFieldChange("ownerName", newValue.name || "");
                      handleCustomFieldChange(
                        "ownerContactNumber",
                        newValue.contactNumber || ""
                      );
                    } else {
                      // Clear the fields when no option is selected
                      handleCustomFieldChange("ownerId", "");
                      handleCustomFieldChange("ownerName", "");
                      handleCustomFieldChange("ownerContactNumber", "");
                    }
                  }}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === "input") {
                      handleCustomFieldChange(
                        "ownerCompanyName",
                        newInputValue
                      );
                      debouncedOwnerSearch(newInputValue);
                      if (!newInputValue) {
                        handleCustomFieldChange("ownerName", "");
                        handleCustomFieldChange("ownerContactNumber", "");
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Company Name"
                      placeholder="Search or enter company name..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {ownerLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      size="small"
                      sx={{
                        ...commonTextFieldProps.sx,
                        fontSize: "0.75rem",
                        "& .MuiInputBase-input": {
                          fontSize: "0.75rem",
                          height: "17px",
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                      <li key={key} {...rest}>
                        <Box sx={{ width: "100%" }}>
                          <Typography
                            variant="body1"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            {option.companyName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            Owner: {option.name} - {option.contactNumber}
                          </Typography>
                        </Box>
                      </li>
                    );
                  }}
                  noOptionsText="No companies found. Enter manually if needed."
                  disabled={disabled}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={ownerOptions}
                  loading={ownerLoading}
                  value={customData.ownerName || null}
                  freeSolo
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return option?.name || option?.companyName || "";
                  }}
                  onChange={(_, newValue) => {
                    if (typeof newValue === "string") {
                      handleCustomFieldChange("ownerName", newValue);
                      debouncedOwnerSearch(newValue);
                    } else if (newValue) {
                      // Update all fields from selected option
                      handleCustomFieldChange("ownerId", newValue.id || "");
                      handleCustomFieldChange("ownerName", newValue.name || "");
                      handleCustomFieldChange(
                        "ownerCompanyName",
                        newValue.companyName || ""
                      );
                      handleCustomFieldChange(
                        "ownerContactNumber",
                        newValue.contactNumber || ""
                      );
                    } else {
                      // Clear the fields when no option is selected
                      handleCustomFieldChange("ownerId", "");
                      handleCustomFieldChange("ownerCompanyName", "");
                      handleCustomFieldChange("ownerContactNumber", "");
                    }
                  }}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === "input") {
                      handleCustomFieldChange("ownerName", newInputValue);
                      debouncedOwnerSearch(newInputValue);
                      if (!newInputValue) {
                        handleCustomFieldChange("ownerCompanyName", "");
                        handleCustomFieldChange("ownerContactNumber", "");
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Owner Name"
                      placeholder="Search or enter owner name..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {ownerLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      size="small"
                      sx={{
                        ...commonTextFieldProps.sx,
                        fontSize: "0.75rem",
                        "& .MuiInputBase-input": {
                          fontSize: "0.75rem",
                          height: "17px",
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                      <li key={key} {...rest}>
                        <Box sx={{ width: "100%" }}>
                          <Typography
                            variant="body1"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            {option.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            Company: {option.companyName}
                          </Typography>
                        </Box>
                      </li>
                    );
                  }}
                  noOptionsText="No companies found. Enter manually if needed."
                  disabled={disabled}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Contact Number"
                  type="tel"
                  value={customData.ownerContactNumber || ""}
                  onChange={(e) =>
                    handleCustomFieldChange(
                      "ownerContactNumber",
                      e.target.value
                    )
                  }
                  fullWidth
                  required={true}
                  disabled={disabled}
                  placeholder="Enter contact number"
                  inputProps={{ maxLength: 10 }}
                  {...commonTextFieldProps}
                />
              </Grid>
            </Grid>
          )}

          {/* Additional Fields - Always shown as required for non-Outsourced types */}
          {showAdditionalFieldsDirectly && (
            <Grid container spacing={1.5} sx={{ mt: 1 }}>
              {additionalFields.map((field: any) => {
                // Handle dependent fields
                if (field.dependsOn) {
                  const dependentValue = customData[field.dependsOn];
                  if (!dependentValue || dependentValue === "Select") {
                    return null; // Don't render if dependency is not met
                  }
                }

                return (
                  <Grid item xs={12} sm={6} md={2} key={field.name}>
                    {field.type === "select" ? (
                      <TextField
                        select
                        label={field.label}
                        value={customData[field.name] || ""}
                        onChange={(e) =>
                          handleCustomFieldChange(field.name, e.target.value)
                        }
                        fullWidth
                        required={field.required}
                        disabled={
                          disabled ||
                          field.disabled ||
                          (field.dependsOn && !customData[field.dependsOn])
                        }
                        {...commonTextFieldProps}
                      >
                        <MenuItem value="" disabled>
                          Select {field.label}
                        </MenuItem>
                        {(field.getOptions
                          ? field.getOptions(customData)
                          : field.options || []
                        ).map((option: string) => (
                          <MenuItem
                            key={option}
                            value={option}
                            sx={{ fontSize: "0.75rem" }}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : field.type === "number" ? (
                      <TextField
                        label={field.label}
                        type="number"
                        value={customData[field.name] || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (
                            field.max &&
                            value.length > String(field.max).length
                          )
                            return;
                          handleCustomFieldChange(field.name, value);
                        }}
                        fullWidth
                        required={field.required}
                        disabled={disabled || field.disabled}
                        inputProps={{
                          min: field.min || undefined,
                          max: field.max || undefined,
                        }}
                        {...commonTextFieldProps}
                      />
                    ) : (
                      <TextField
                        label={field.label}
                        type={field.type || "text"}
                        value={customData[field.name] || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (field.maxLength && value.length > field.maxLength)
                            return;
                          handleCustomFieldChange(field.name, value);
                        }}
                        fullWidth
                        required={field.required}
                        disabled={disabled || field.disabled}
                        placeholder={field.placeholder}
                        inputProps={{
                          maxLength: field.maxLength || undefined,
                        }}
                        {...commonTextFieldProps}
                      />
                    )}
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Add Additional Details Button - Only for Outsourced */}
          {isMarketLoad && (
            <Box sx={{ mt: 1, mb: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setShowOptionalFields(!showOptionalFields)}
                size={smallButtonProp.size}
                sx={{
                  ...smallButtonProp.sx,
                  color: "grey",
                  borderColor: "grey.400",
                  "&:hover": {
                    borderColor: "grey.400",
                    color: "grey",
                  },
                }}
              >
                {showOptionalFields
                  ? "Hide Additional Details"
                  : "Add Additional Details"}
              </Button>
            </Box>
          )}

          {/* Optional Fields - Only for Outsourced when button is clicked */}
          {isMarketLoad && showOptionalFields && (
            <Grid container spacing={1.5}>
              {additionalFields.map((field: any) => {
                // Handle dependent fields
                if (field.dependsOn) {
                  const dependentValue = customData[field.dependsOn];
                  if (!dependentValue || dependentValue === "Select") {
                    return null; // Don't render if dependency is not met
                  }
                }

                return (
                  <Grid item xs={12} sm={6} md={2} key={field.name}>
                    {field.type === "select" ? (
                      <TextField
                        select
                        label={field.label}
                        value={customData[field.name] || ""}
                        onChange={(e) =>
                          handleCustomFieldChange(field.name, e.target.value)
                        }
                        fullWidth
                        required={field.required}
                        disabled={
                          disabled ||
                          field.disabled ||
                          (field.dependsOn && !customData[field.dependsOn])
                        }
                        {...commonTextFieldProps}
                      >
                        <MenuItem value="" disabled>
                          Select {field.label}
                        </MenuItem>
                        {(field.getOptions
                          ? field.getOptions(customData)
                          : field.options || []
                        ).map((option: string) => (
                          <MenuItem
                            key={option}
                            value={option}
                            sx={{ fontSize: "0.75rem" }}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : field.type === "number" ? (
                      <TextField
                        label={field.label}
                        type="number"
                        value={customData[field.name] || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (
                            field.max &&
                            value.length > String(field.max).length
                          )
                            return;
                          handleCustomFieldChange(field.name, value);
                        }}
                        fullWidth
                        required={field.required}
                        disabled={disabled || field.disabled}
                        inputProps={{
                          min: field.min || undefined,
                          max: field.max || undefined,
                        }}
                        {...commonTextFieldProps}
                      />
                    ) : (
                      <TextField
                        label={field.label}
                        type={field.type || "text"}
                        value={customData[field.name] || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (field.maxLength && value.length > field.maxLength)
                            return;
                          handleCustomFieldChange(field.name, value);
                        }}
                        fullWidth
                        required={field.required}
                        disabled={disabled || field.disabled}
                        placeholder={field.placeholder}
                        inputProps={{
                          maxLength: field.maxLength || undefined,
                        }}
                        {...commonTextFieldProps}
                      />
                    )}
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Grid>
      )}

      {selectedReference && !isManualEntry && (
        <Grid item xs={12}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "primary.light",
              bgcolor: "#F0F7FF",
            }}
          >
            <Typography
              variant="subtitle2"
              color="primary.dark"
              sx={{ fontSize: "0.75rem" }}
              gutterBottom
            >
              {formatText(category)} Details (From Master Data)
            </Typography>
            <Grid container spacing={1}>
              {Object.entries(selectedReference).map(([key, value]) => {
                if (
                  [
                    "id",
                    "status",
                    "aadharNumber",
                    "acquisitionType",
                    "chassisNumber",
                    "makeYear",
                    "model",
                    "color",
                    "joiningDate",
                    "address",
                  ].includes(key)
                )
                  return null;
                if (key === "isAC") {
                  return (
                    <Grid item xs={12} sm={3} key={key}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        AC: {value ? "Yes" : "No"}
                      </Typography>
                    </Grid>
                  );
                }
                if (key === "vehicleNumber") {
                  return (
                    <Grid item xs={12} sm={3} key={key}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Vehicle Number: {formatVehicleNumber(value)}
                      </Typography>
                    </Grid>
                  );
                }
                return (
                  <Grid item xs={12} sm={3} key={key}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      {formatText(key)}: {formatText(value)}
                    </Typography>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default ReferenceSelectionFields;
